import { useLayoutEffect, useState, type CSSProperties, type RefObject } from "react";
import { getLayoutWidth } from "@/shared/utils/layoutViewport";

/** Only phones line the pill up with the tiles; tablets and desktop keep the default row padding. */
const PHONE_MAX_LAYOUT_WIDTH = 767;

/** Left edge of an element's layout box in page coordinates, ignoring CSS transforms. */
const layoutPageLeft = (el: HTMLElement): number => {
  let left = 0;
  for (let node: HTMLElement | null = el; node; node = node.offsetParent as HTMLElement | null) left += node.offsetLeft;
  return left;
};

/**
 * Horizontal padding that lines a footer row's content up with the outer edges of the
 * `.track-tile` cards inside `targetRef`, so a full-width Next/Checkout pill starts and
 * ends exactly where the tiles above it do.
 *
 * Tiles are measured by layout position (offsetLeft/offsetWidth), not their painted
 * box, so their entrance, hover and selected-state transforms do not skew the result.
 * Returns no style on tablets and desktop, or when there is no target, and the row keeps
 * its default padding.
 */
export function useAlignedRowInsets(targetRef?: RefObject<HTMLElement>) {
  const [row, setRow] = useState<HTMLElement | null>(null);
  const [insetStyle, setInsetStyle] = useState<CSSProperties>();

  useLayoutEffect(() => {
    const target = targetRef?.current;
    if (!row || !target) {
      setInsetStyle(undefined);
      return;
    }

    const measure = () => {
      if (getLayoutWidth() > PHONE_MAX_LAYOUT_WIDTH) return setInsetStyle(undefined);

      const tiles = Array.from(target.querySelectorAll<HTMLElement>(".track-tile")).filter((tile) => tile.offsetWidth > 0);
      if (!tiles.length) return setInsetStyle(undefined);

      const tilesLeft = Math.min(...tiles.map(layoutPageLeft));
      const tilesRight = Math.max(...tiles.map((tile) => layoutPageLeft(tile) + tile.offsetWidth));

      const rowLeft = row.getBoundingClientRect().left + window.scrollX;
      const rowRight = rowLeft + row.offsetWidth;
      setInsetStyle({
        paddingLeft: Math.max(0, Math.round(tilesLeft - rowLeft)),
        paddingRight: Math.max(0, Math.round(rowRight - tilesRight)),
      });
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(target);
    observer.observe(row);
    window.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [row, targetRef]);

  return { rowRef: setRow, insetStyle };
}
